import { useRef, useEffect } from "react";
import DOMPurify from "dompurify";
import { TextB, TextItalic, TextUnderline, TextHOne, TextHTwo, ListBullets, ListNumbers, TextT } from "@phosphor-icons/react";

// Batch H: formatted "Intelligence" fields — headings, paragraphs, bullets,
// numbered lists, bold/italic/underline. Everything is sanitized on input
// (paste + typing) AND again on display, matching the backend allowlist.
const ALLOWED = { ALLOWED_TAGS: ["h1", "h2", "h3", "p", "br", "ul", "ol", "li", "b", "strong", "i", "em", "u"], ALLOWED_ATTR: [] };
export const sanitizeRichText = (html) => DOMPurify.sanitize(html || "", ALLOWED);

const VIEW_CLASSES = "rich-text-view text-sm leading-relaxed text-[#3F3F46] " +
  "[&_h1]:mt-3 [&_h1]:mb-1 [&_h1]:font-heading [&_h1]:text-base [&_h1]:font-black [&_h1]:text-[#032852] " +
  "[&_h2]:mt-3 [&_h2]:mb-1 [&_h2]:font-heading [&_h2]:text-sm [&_h2]:font-black [&_h2]:text-[#032852] " +
  "[&_h3]:mt-2 [&_h3]:mb-0.5 [&_h3]:text-sm [&_h3]:font-bold [&_h3]:text-[#032852] " +
  "[&_p]:my-1.5 [&_ul]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5";

export function RichTextView({ html, testId, className = "" }) {
  const clean = sanitizeRichText(html);
  if (!clean.trim()) return null;
  return <div className={`${VIEW_CLASSES} ${className}`} data-testid={testId}
    dangerouslySetInnerHTML={{ __html: clean }} />;
}

function ToolBtn({ icon: Icon, label, onClick }) {
  return (
    <button type="button" title={label} onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      className="rounded-sm p-1.5 text-[#52525B] transition-colors hover:bg-[#032852]/10 hover:text-[#032852]"
      data-testid={`rt-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <Icon size={15} weight="bold" />
    </button>
  );
}

export function RichTextEditor({ value, onChange, testId, placeholder = "Add organized research notes…" }) {
  const ref = useRef(null);
  // Initialize once; afterwards the DOM is the source of truth while typing.
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = sanitizeRichText(value || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const emit = () => onChange(sanitizeRichText(ref.current?.innerHTML || ""));
  const cmd = (command, arg = null) => { document.execCommand(command, false, arg); emit(); ref.current?.focus(); };
  const onPaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData("text/html") || e.clipboardData.getData("text/plain").replace(/\n/g, "<br/>");
    document.execCommand("insertHTML", false, sanitizeRichText(html));
    emit();
  };
  return (
    <div className="rounded-sm border border-[#E4E4E7] bg-white">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-[#F4F4F5] px-1.5 py-1">
        <ToolBtn icon={TextHOne} label="Heading" onClick={() => cmd("formatBlock", "<h2>")} />
        <ToolBtn icon={TextHTwo} label="Subheading" onClick={() => cmd("formatBlock", "<h3>")} />
        <ToolBtn icon={TextT} label="Paragraph" onClick={() => cmd("formatBlock", "<p>")} />
        <span className="mx-1 h-4 w-px bg-[#E4E4E7]" />
        <ToolBtn icon={TextB} label="Bold" onClick={() => cmd("bold")} />
        <ToolBtn icon={TextItalic} label="Italic" onClick={() => cmd("italic")} />
        <ToolBtn icon={TextUnderline} label="Underline" onClick={() => cmd("underline")} />
        <span className="mx-1 h-4 w-px bg-[#E4E4E7]" />
        <ToolBtn icon={ListBullets} label="Bullet list" onClick={() => cmd("insertUnorderedList")} />
        <ToolBtn icon={ListNumbers} label="Numbered list" onClick={() => cmd("insertOrderedList")} />
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning role="textbox" aria-multiline="true"
        onInput={emit} onBlur={emit} onPaste={onPaste} data-testid={testId}
        data-placeholder={placeholder}
        className={`min-h-[120px] max-h-[320px] overflow-y-auto px-3 py-2 outline-none focus:ring-1 focus:ring-[#569A1F]/40 ${VIEW_CLASSES} empty:before:text-[#9CA3AF] empty:before:content-[attr(data-placeholder)]`} />
    </div>
  );
}
